import api, { auth } from './api'
import url from './url'
import { canonicalInstall, choosePwaInstallation, deviceWorkerScope, getPwaInstallation, standalonePwa } from './pwa-installation'

type Credentials = { user?:string, token?:string }
export type DeviceConsent = { id:string, deviceId:string, binding:string, owner:string, enabled:boolean, needsDisable?:boolean, needsLocalCleanup?:boolean, pendingActivation?:boolean }
export type DeviceInfo = { id:string, deviceId:string, binding:string, install:string, origin:string, createdAt:number }
export type DeviceState = { publicKey?:string, devices:DeviceInfo[], ready:boolean, enabled:boolean, install?:string, legacy:boolean }
const consentKey = (install: string) => `freshman-device-notifications-v1:${install}`
const uuid = () => crypto.randomUUID()
const browserStorage = () => { try { return window.localStorage } catch { return undefined } }
const sameOwner = (left:Credentials, right:Credentials) => left.user === right.user && left.token === right.token
const accountHeaders = (owner:Credentials) => ({ 'X-Freshman-Auth-User':owner.user, 'X-Freshman-Auth-Token':owner.token })
const base64Bytes = (text: string) => Uint8Array.from(atob(text.replace(/-/g, '+').replace(/_/g, '/')), char => char.charCodeAt(0))
const hashEndpoint = async (text: string) => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)))).map(byte => byte.toString(16).padStart(2, '0')).join('')
export const deviceNotificationSupport = () => Boolean(window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window)
export const iosNeedsHomeScreen = () => !standalonePwa() && (/iPad|iPhone|iPod/.test(navigator.userAgent) || navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

export function readDeviceConsent(install: string, storage=browserStorage()): DeviceConsent | undefined {
  try {
    const value = JSON.parse(storage?.getItem(consentKey(install)) || 'null')
    if (value && /^[a-f0-9]{64}$/.test(value.id) && typeof value.owner === 'string' && typeof value.binding === 'string' && typeof value.deviceId === 'string') return value
  } catch {}
}
function writeConsent(install:string, value:DeviceConsent, storage=browserStorage()) {
  if (!storage) throw Error('Allow browser storage to enable device notifications.')
  storage.setItem(consentKey(install), JSON.stringify(value))
}

export async function activeDeviceWorker(registration: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> {
  if (registration.active?.state === 'activated') return registration
  return new Promise((resolve, reject) => {
    const workers = new Set<ServiceWorker>()
    const cleanup = () => { clearTimeout(timer); registration.removeEventListener('updatefound', watch); workers.forEach(worker => worker.removeEventListener('statechange', check)) }
    const check = () => { if (registration.active?.state === 'activated') { cleanup(); resolve(registration) } }
    const watch = () => { const worker=registration.installing || registration.waiting || registration.active; if (worker && !workers.has(worker)) { workers.add(worker); worker.addEventListener('statechange', check) }; check() }
    const timer=setTimeout(() => { cleanup(); reject(Error('The notification service did not start. Try again.')) }, 20_000)
    registration.addEventListener('updatefound', watch); watch()
  })
}
export async function setDeviceWorkerBinding(registration:ServiceWorkerRegistration, binding:string | null, previousBinding?:string, allowEmpty=false, mutation?:string) {
  const worker=registration.active
  if (!worker) throw Error('The notification service is not ready.')
  await new Promise<void>((resolve, reject) => {
    const channel = new MessageChannel()
    const finish = (error?:Error) => { clearTimeout(timer); channel.port1.close(); error ? reject(error) : resolve() }
    const timer=setTimeout(() => finish(Error('The notification service did not save this device. Try again.')), 10_000)
    channel.port1.onmessage = event => finish(event.data?.saved ? undefined : Error(event.data?.changed === false ? 'Device notifications changed. Try again.' : 'Allow browser storage to enable device notifications.'))
    worker.postMessage({ type:'freshman:device-binding', binding, previousBinding, allowEmpty, mutation }, [channel.port2])
  })
}

// Dependency injection keeps account switches and delayed platform APIs testable.
export function createDeviceNotificationClient({ request=api, readAuth=()=>auth.get(), serviceWorkers=navigator.serviceWorker,
  notification=window.Notification, storage=browserStorage(), readInstall=getPwaInstallation, origin=location.origin,
  id=uuid, hash=hashEndpoint, bind=setDeviceWorkerBinding, lifecycle=(window as any).deviceNotificationLifecycle } = {}) {
  let registration:ServiceWorkerRegistration, state:DeviceState={ devices:[], ready:false, enabled:false, legacy:false }, generation=0
  const get = (owner:Credentials) => request.get('/notify/device', { headers:accountHeaders(owner), ms:10_000 })
  const post = (path:string, value:object, owner:Credentials) => request.post(`/notify/device/${path}`, value, { headers:accountHeaders(owner), ms:10_000 })
  const ensureOwner = (owner:Credentials) => { if (!sameOwner(owner, readAuth())) throw Error('Sign-in changed. Try again for the current account.'); lifecycle.assertOwner(owner) }
  const payload = (install:string, record:DeviceConsent, subscription:PushSubscription) => ({ install, origin, deviceId:record.deviceId, binding:record.binding, subscription:subscription.toJSON() })
  async function release(owner:Credentials, next:Credentials) {
    generation++
    const install=readInstall().key
    if (owner.user !== next.user) state={ devices:[], ready:false, enabled:false, legacy:readInstall().legacy, install }
    await lifecycle.depart(owner,next)
  }
  async function load() {
    const owner={ ...readAuth() }, installation=readInstall(), install=installation.key
    state={ devices:[], ready:false, enabled:false, install, legacy:installation.legacy }
    if (!install) return state
    if (!serviceWorkers || !notification || !storage) return state
    return lifecycle.withScope(install, async lease => {
      await lifecycle.reconcile(owner,install,true)
      if (!owner.user || !owner.token) return state
      ensureOwner(owner)
      const revision=++generation
      const result=await get(owner)
      if (!sameOwner(owner, readAuth()) || revision !== generation) return state
      state={ ...state, ...result, install, legacy:installation.legacy, enabled:false }
      if (!install || !serviceWorkers || !notification || !storage || !result.publicKey) return state
      const prepared=await activeDeviceWorker(await serviceWorkers.register(`/device-notifications-sw.js?install=${encodeURIComponent(install)}`, { scope:deviceWorkerScope(install), updateViaCache:'none' }))
      await lifecycle.prepareWorker(prepared)
      ensureOwner(owner)
      if (revision !== generation || readInstall().key !== install) return state
      registration=prepared
      const subscription=await registration.pushManager.getSubscription(), remembered=readDeviceConsent(install, storage)
      if (revision !== generation || readInstall().key !== install) return state
      let enabled=false
      if (remembered?.enabled && remembered.owner === owner.user) {
        const old=result.devices.find(device => device.id === remembered.id && device.binding === remembered.binding && device.install === install && device.origin === origin)
        if (old && subscription && notification.permission === 'granted') {
          const synced=await post('sync', { ...payload(install, remembered, subscription), previousId:old.id }, owner)
          ensureOwner(owner)
          if (revision !== generation || readInstall().key !== install) return state
          const latest=readDeviceConsent(install,storage)
          // A second view can disable/re-enable the same registration while this
          // request is in flight. Never restore its older consent or binding.
          if (!latest?.enabled || latest.owner !== owner.user || latest.binding !== remembered.binding || latest.id !== remembered.id) {
            state={...state,ready:true,enabled:Boolean(latest?.enabled && latest.owner === owner.user)}
            return state
          }
          enabled=Boolean(synced.enabled)
          if (enabled) {
            await lease.assert();ensureOwner(owner)
            if (lifecycle.blocked(install,remembered.binding)) throw Error('Device notifications changed. Try again.')
            await bind(registration, remembered.binding, remembered.binding, true, lease.owner)
            ensureOwner(owner);await lease.assert()
            const current=readDeviceConsent(install,storage)
            if (!current?.enabled || current.binding!==remembered.binding || lifecycle.blocked(install,remembered.binding)) throw Error('Device notifications changed. Try again.')
            writeConsent(install, { ...current, id:synced.id }, storage)
          } else {
            const entry=lifecycle.mark(install,remembered)
            await lifecycle.suppress(entry);lifecycle.finish(entry)
          }
        } else {
          const current=readDeviceConsent(install,storage)
          if(current?.enabled && current.binding===remembered.binding) {
            const entry=lifecycle.mark(install,current)
            await lifecycle.suppress(entry)
            if(!old)lifecycle.finish(entry)
          }
        }
      }
      ensureOwner(owner)
      if (revision !== generation || readInstall().key !== install) return state
      state={ ...state, ready:true, enabled }
      return state
    })
  }
  async function enable() {
    const owner={ ...readAuth() }, install=readInstall().key, worker=registration
    if (!owner.user || !owner.token || !install || !worker || worker.scope !== new URL(deviceWorkerScope(install),origin).href || !state.ready || !state.publicKey) throw Error('Wait for device notifications to load, then try again.')
    // No asynchronous setup precedes the permission request: iOS requires this
    // call to be a direct consequence of the person's tap.
    const permission=await notification.requestPermission()
    ensureOwner(owner)
    if (permission !== 'granted') throw Error('notifications blocked · allow in device or browser settings')
    return lifecycle.withScope(install, async lease => {
      await lifecycle.reconcile(owner,install,true);ensureOwner(owner)
      let subscription=await worker.pushManager.getSubscription()
      ensureOwner(owner)
      const key=base64Bytes(state.publicKey), previousKey=subscription?.options.applicationServerKey
      if (previousKey && Array.from(new Uint8Array(previousKey)).join() !== Array.from(key).join()) throw Error('This device uses another notification key. Disable its old subscription in browser settings before enabling it again.')
      subscription ||= await worker.pushManager.subscribe({ userVisibleOnly:true, applicationServerKey:key })
      ensureOwner(owner)
      const old=readDeviceConsent(install, storage), record:DeviceConsent={ id:await hash(subscription.endpoint), deviceId:old?.deviceId || id(), binding:id(), owner:owner.user, enabled:true }
      ensureOwner(owner)
      if(old?.enabled || old?.needsDisable) {
        const previous=lifecycle.mark(install,old);await lifecycle.suppress(previous)
        void lifecycle.remote(previous,owner).catch(()=>{})
      }
      await lease.assert();ensureOwner(owner)
      // Prove storage works before the server enables delivery. A pending record
      // also lets a concurrent sign-out find this exact activation.
      writeConsent(install,{...record,enabled:false,needsDisable:true,pendingActivation:true},storage)
      let saved=false
      try {
        const result=await post('enable', payload(install, record, subscription), owner);saved=true
        record.id=result.id
        const current=()=>readDeviceConsent(install,storage)?.binding===record.binding && !lifecycle.blocked(install,record.binding)
        ensureOwner(owner);await lease.assert()
        if(!current())throw Error('Device notifications changed. Try again.')
        await bind(worker, record.binding, undefined, false, lease.owner)
        ensureOwner(owner);await lease.assert()
        if(!current())throw Error('Device notifications changed. Try again.')
        writeConsent(install, { ...record, needsDisable:false }, storage)
      } catch (error) {
        const entry=lifecycle.mark(install,{...record,pendingActivation:!saved})
        await lifecycle.suppress(entry)
        if(saved && await lifecycle.remote(entry,owner).catch(()=>false))lifecycle.finish(entry)
        throw error
      }
      state={ ...state, enabled:true }
      return state
    })
  }
  async function disable(device?:DeviceInfo) {
    const owner={ ...readAuth() }, install=readInstall().key, remembered=install && readDeviceConsent(install, storage)
    const target=device || remembered
    if (!target) return state
    const targetInstall=device?.install || install, local=targetInstall && readDeviceConsent(targetInstall,storage)
    const entry=local?.id===target.id && local.binding===target.binding && (!device || device.origin===origin) ? lifecycle.mark(targetInstall,local) : undefined
    if(entry)await lifecycle.suppress(entry)
    return lifecycle.withScope(targetInstall,async lease=>{
      ensureOwner(owner);await lease.assert()
      await post('disable', { id:target.id, binding:target.binding }, owner)
      ensureOwner(owner)
      if(entry)lifecycle.finish(entry)
      const current=install && readDeviceConsent(install,storage)
      state={ ...state, enabled:Boolean(current?.enabled && current.owner===owner.user), devices:state.devices.filter(value => value.id !== target.id || value.binding!==target.binding) }
      return state
    })
  }
  const test = async () => {
    const owner={ ...readAuth() }, install=readInstall().key, record=install && readDeviceConsent(install, storage)
    if (!state.enabled || !record || record.owner !== owner.user) throw Error('Enable this device first.')
    await post('test', { id:record.id, binding:record.binding }, owner); ensureOwner(owner)
  }
  return { load, enable, disable, release, test, state:()=>state }
}

let installed=false
export function initializeDeviceNotifications() {
  if (installed || !deviceNotificationSupport() || iosNeedsHomeScreen()) return
  installed=true
  const client=createDeviceNotificationClient()
  let previous={ ...auth.get() }
  auth.add(next => {
    const captured=previous; previous={ ...next }
    // Shared auth writers own departure; a credential refresh of the same
    // principal instead reconciles its existing activation with the new token.
    if (!sameOwner(captured, next)) void client.load().catch(()=>{})
  })
  const install=getPwaInstallation().key, record=install && readDeviceConsent(install)
  if (record?.enabled || record?.needsDisable) void client.load().catch(()=>{})
  // Notification clicks can find an uncontrolled page because these workers
  // use reserved scopes and deliberately do not intercept application fetches.
  navigator.serviceWorker.addEventListener('message', event => {
    const data=event.data, key=getPwaInstallation().key, owner=auth.get(), consent=key && readDeviceConsent(key)
    if (!['freshman:probe-notification','freshman:open-notification'].includes(data?.type) || !event.ports[0] || key !== canonicalInstall(data.install)
      || !consent?.enabled || consent.owner !== owner.user || consent.binding !== data.binding) return
    if (data.type === 'freshman:probe-notification') { event.ports[0].postMessage({matches:true});return }
    let destination:URL
    try { destination=new URL(data.url, location.origin); if (destination.origin !== location.origin) return } catch { return }
    url.push(destination.pathname + destination.search + destination.hash)
    event.ports[0].postMessage({ handled:true })
  })
}
export { choosePwaInstallation }

export async function showDeviceNotification({ app, title, body, link, tag, icon }:{ app:string, title?:string, body:string, link?:string, tag?:string, icon?:string }):Promise<'shown'|'filtered'|'unsupported'> {
  const install=getPwaInstallation().key, canonical=canonicalInstall(app) || app
  if (install && install !== 'base' && install !== canonical) return 'filtered'
  const consent=install && readDeviceConsent(install), owner={...auth.get()}
  if (consent && (!consent.enabled || consent.owner !== owner.user)) return 'filtered'
  if (!install || !deviceNotificationSupport() || Notification.permission !== 'granted') return 'unsupported'
  if (!consent?.enabled || consent.owner !== owner.user) return 'unsupported'
  const registration=await navigator.serviceWorker.getRegistration(new URL(deviceWorkerScope(install), location.origin).href)
  if (!registration?.active || registration.scope !== new URL(deviceWorkerScope(install), location.origin).href) return 'unsupported'
  if (auth.get().user !== owner.user || auth.get().token !== owner.token) throw Error('Sign-in changed before notification display.')
  const remembered=readDeviceConsent(install)
  if (!remembered?.enabled || remembered.binding !== consent.binding) throw Error('Device notifications changed before display.')
  let destination:URL
  try {
    destination=new URL(link?.startsWith('/') || link?.includes('://') ? link : link ? `https://${link}` : `/${app}`, location.origin)
    // Existing desktop navigation strips the site's custom domains. Keep the
    // same local path when displaying a persistent device notification.
    destination=new URL(destination.pathname + destination.search + destination.hash, location.origin)
  } catch { destination=new URL('/notify', location.origin) }
  destination.searchParams.set('pwa', install)
  const icons={ wordbase:'/raw/wordbase/favicon256.png', lettercomb:'/raw/capitals/icon.png', letterpress:'/raw/letterpress/icon.png', greeter:'/raw/greeter/icon.png', chat:'/raw/chat/icon.png', light:'/raw/light/icon.png', quadbase:'/raw/quadbase/icon-small.png' }
  await registration.showNotification(title || `${canonical} · freshman.dev`, { body, tag:tag || link || app, icon:icon || icons[canonical] || '/base-icon.png',
    data:{ url:destination.href, install, binding:consent.binding } })
  if (auth.get().user !== owner.user || auth.get().token !== owner.token || readDeviceConsent(install)?.binding !== consent.binding || !readDeviceConsent(install)?.enabled) {
    for (const notice of await registration.getNotifications()) if (notice.data?.binding === consent.binding && notice.tag === (tag || link || app)) notice.close()
    throw Error('Sign-in changed before notification display completed.')
  }
  return 'shown'
}
